/**
 * Sentry Error Tracking Integration
 * 
 * To enable Sentry:
 * 1. Install: npm install @sentry/react
 * 2. Set REACT_APP_SENTRY_DSN in .env files
 * 
 * Until installed, this module safely no-ops all operations.
 */

let Sentry = null;

/**
 * Initialize Sentry error tracking
 * Set REACT_APP_SENTRY_DSN in .env files to enable
 */
export async function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  const environment = process.env.NODE_ENV;

  // Only initialize if DSN is provided
  if (!dsn) {
    return;
  }

  try {
    // Dynamically import Sentry only when needed
    const SentryModule = await import('@sentry/react');
    Sentry = SentryModule;

    Sentry.init({
      dsn,
      environment,
      
      // Set sample rates
      tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,

      // Integrations
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Filter out common browser errors
      beforeSend(event, hint) {
        const error = hint.originalException;
        
        // Ignore certain errors
        if (error && error.message) {
          // Network errors handled by app
          if (error.message.match(/NetworkError|Failed to fetch/i)) {
            return null;
          }
          
          // Extension errors
          if (error.message.match(/chrome-extension|moz-extension/i)) {
            return null;
          }
        }

        return event;
      },
    });
  } catch (error) {
    // Silently fail if Sentry package not installed
  }
}

/**
 * Capture exception manually
 */
export function captureException(error, context = {}) {
  if (Sentry && process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureException(error, { extra: context });
  }
}

/**
 * Set user context for error reports
 */
export function setUserContext(user) {
  if (!Sentry) return;
  
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.userId,
    email: user.email,
    username: user.fullName,
  });
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message, data = {}) {
  if (!Sentry) return;
  
  Sentry.addBreadcrumb({
    message,
    level: 'info',
    data,
  });
}

export default Sentry;
